<template>
  <div class="content">
    <div class="content-preloader" v-if="state.preloader">
      <PreloaderComponent />
    </div>
    <template v-else>
      <component :is="layout">
        <router-view />
      </component>
    </template>

    <SnackbarComponent />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, reactive, inject } from 'vue';
import { useRoute, RouteLocationNormalizedLoaded } from 'vue-router';
import SnackbarComponent from '@/components/shared/SnackbarComponent.vue';
import Default from '@/components/layouts/Default.vue';
import { useAuthStore } from '@/store/authStore';
import { refreshUser, getDataUser } from '@/api/authService';
import { errorsObjectType } from '@/types/common';
import { errorsConst } from '@/constants/common';
import { createClassUser } from '@/utils/getAuth';
import PreloaderComponent from '@/components/shared/PreloaderComponent.vue';
import { showError } from '@/utils/errorsInterceptor';

type stateType = {
  errors: errorsObjectType;
  preloader: boolean;
};

export default defineComponent({
  name: 'App',
  components: {
    Default,
    SnackbarComponent,
    PreloaderComponent,
  },
  setup() {
    const axios: any = inject('axios');
    const route: RouteLocationNormalizedLoaded = useRoute();
    const layout = computed(() => route?.meta.layout || 'Default');
    const store = useAuthStore();

    const state: stateType = reactive({
      errors: errorsConst,
      preloader: false,
    });

    const refreshSession = async (refreshToken: string) => {
      try {
        state.preloader = true;
        const response = await refreshUser({
          refresh_token: refreshToken,
        });
        if (response) {
          store.setTokens(response.data);
          localStorage.setItem('access_token', response.data.access_token);
          axios.defaults.headers.common['auth-token'] =
            response.data.access_token;
          await loadMe();
        }
      } catch (error) {
        store.logout();
        showError(error);
      } finally {
        state.preloader = false;
      }
    };

    const init = async (): Promise<void> => {
      createClassUser();
      let refreshTokenLS = localStorage.getItem('refresh_token');
      if (refreshTokenLS) {
        const access_token = JSON.parse(refreshTokenLS);
        refreshSession(access_token);
      }
    };
    init();

    const loadMe = async () => {
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      if (accessToken && refreshToken) {
        try {
          const response = await getDataUser();
          if (response) {
            store.setUser(response.data, true);
          }
        } catch (error) {
          showError(error);
        }
      }
    };

    return {
      layout,
      state,
    };
  },
});
</script>

<style lang="scss">
.content {
  overflow: hidden;
  height: 100vh;
}
.content-preloader {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
}
</style>
